/**
 * Bug Reporter client (Issue #887)
 *
 * - Installs a lightweight console.error ring buffer on import.
 * - Hooks window.onerror / unhandledrejection.
 * - Exposes submitBugReport(), getRecentConsoleErrors(), collectContext().
 */

import { apiRequest } from './api.js';
import { importOptional } from '../lib/importOptional.js';

const RING_MAX = 20;
const ring = [];
let installed = false;

function push(entry) {
  try {
    ring.push({ t: new Date().toISOString(), m: entry });
    while (ring.length > RING_MAX) ring.shift();
  } catch (_e) {
    /* ignore ring buffer errors */
  }
}

export function installConsoleErrorCapture() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  try {
    const origError = console.error.bind(console);
    console.error = (...args) => {
      try {
        const flat = args.map((a) => {
          if (a instanceof Error) return `${a.name}: ${a.message}`;
          if (typeof a === 'object') {
            try { return JSON.stringify(a).slice(0, 500); } catch (_e) { return String(a); }
          }
          return String(a);
        }).join(' ');
        push(flat);
      } catch (_e) { /* ignore */ }
      origError(...args);
    };
  } catch (_e) {
    /* ignore */
  }

  try {
    window.addEventListener('error', (ev) => {
      const msg = ev?.error?.stack || ev?.message || 'window.error';
      push(`window.error: ${String(msg).slice(0, 500)}`);
    });
    window.addEventListener('unhandledrejection', (ev) => {
      const msg = ev?.reason?.stack || ev?.reason?.message || String(ev?.reason || 'unhandledrejection');
      push(`unhandledrejection: ${String(msg).slice(0, 500)}`);
    });
  } catch (_e) { /* ignore */ }
}

export function getRecentConsoleErrors() {
  return ring.slice();
}

function detectOs(ua) {
  if (!ua) return 'unknown';
  if (/Windows NT/.test(ua)) return 'Windows';
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/Android/.test(ua)) return 'Android';
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'unknown';
}

export function collectContext(user = null) {
  if (typeof window === 'undefined') return {};
  const ua = navigator?.userAgent || '';
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  return {
    url: window.location.href,
    route: window.location.pathname + window.location.search,
    userAgent: ua,
    metadata: {
      os: detectOs(ua),
      deviceType: isMobile ? 'mobile' : 'desktop',
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      userAgent: ua,
      consoleErrors: getRecentConsoleErrors(),
      role: user?.role || null,
      email: user?.email || null,
      orgSlug: user?.organization_slug || user?.organization?.slug || null,
      orgId: user?.organization_id || null,
      userId: user?.id || null,
      timestamp: new Date().toISOString()
    }
  };
}

/**
 * Capture a screenshot of the current viewport using html2canvas.
 * Returns a base64 JPEG data URL, or null on failure.
 */
export async function captureScreenshot() {
  if (typeof window === 'undefined') return null;
  try {
    // #4231: a stale-chunk failure is typed; the report is still submitted, just without a screenshot (null).
    const { default: html2canvas } = await importOptional(() => import('html2canvas'), 'html2canvas');
    const target = document.querySelector('[data-testid="app-content"]')
      || document.querySelector('.App')
      || document.body;
    const canvas = await html2canvas(target, {
      useCORS: true,
      ignoreElements: (el) => el.hasAttribute('data-html2canvas-ignore')
    });

    // Scale down to max 1280px longest side
    const maxDim = 1280;
    let out = canvas;
    if (canvas.width > maxDim || canvas.height > maxDim) {
      const scale = maxDim / Math.max(canvas.width, canvas.height);
      const scaled = document.createElement('canvas');
      scaled.width = Math.round(canvas.width * scale);
      scaled.height = Math.round(canvas.height * scale);
      scaled.getContext('2d').drawImage(canvas, 0, 0, scaled.width, scaled.height);
      out = scaled;
    }
    return out.toDataURL('image/jpeg', 0.7);
  } catch (err) {
    console.warn('[bug-reporter] screenshot capture failed:', err?.message);
    return null;
  }
}

/**
 * Submit a bug report.
 * @param {{ note, url, route, userAgent, metadata, screenshot, clientMessageId? }} payload
 */
export async function submitBugReport(payload) {
  return apiRequest('/api/bug-reports', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

// ---------------------------------------------------------------------------
// Persist-and-retry queue (Issue #2611)
//
// A learner on a flaky connection can have the POST to /api/bug-reports throw
// a network error (safeFetch's FetchError with status 0 — see
// utils/safeFetch.ts) after already typing a report. Before this queue
// existed that report was gone: the raw fetch error was shown and nothing
// was persisted (measured for user 910, #2611).
//
// This mirrors #1136's `useDraftPersistence` chat-draft pattern rather than
// reinventing it: a stable client-generated id so a retry is idempotent
// server-side (Migration 0409 / insertBugReport's dedup path above this
// comment... see bugReports.js), and "clear only after the server confirms"
// — an item is removed from the queue ONLY on a successful (2xx) resubmit,
// never speculatively. Unlike the single-draft-per-run shape of
// useDraftPersistence, bug reports have no natural "run" to key off and can
// queue up more than one at a time, so this stores an ARRAY of pending
// reports under one key rather than one entry per id.
//
// Same environment-degrade contract as useDraftPersistence: if localStorage
// is unavailable / blocked / quota-exceeded, queuing silently no-ops (no
// thrown errors to the caller) and the report is lost exactly as it would
// have been before this fix — never worse.
const PENDING_STORAGE_KEY = 'tacit_pending_bug_reports';
// mega100 review-fix — three defects in the original queue, all here:
//
// (a) CROSS-ACCOUNT LEAK. This key is per-BROWSER, not per-account, and
//     AuthContext.logout clears no localStorage. Entries carried no owner, and
//     the flush effect re-fires whenever an eligible user signs in — so user
//     A's queued report (which carries A's email, userId, orgId, orgSlug, role,
//     console errors and a screenshot of A's viewport, via collectContext) was
//     POSTed under user B's session and filed as B's row in B's org. Every
//     entry now records `ownerId` at enqueue time and the flush only sends
//     entries belonging to the caller's own user id.
// (b) A null `clientMessageId` (no crypto.randomUUID — any non-secure context)
//     made the dedupe filter match EVERY other null-id entry, collapsing the
//     queue to one and destroying earlier reports. Queue bookkeeping now uses
//     `entryKey`, which is always unique; clientMessageId is only the server's
//     dedup key and is allowed to be null.
// (c) clientMessageId was minted only on the retry path, so the FIRST POST
//     carried none — and migration 0409's index is partial
//     (WHERE client_message_id IS NOT NULL), so a NULL-keyed first row could
//     never be matched by the retry's dedup lookup. Callers now mint the id
//     before the first submit; see newClientMessageId().
let entryKeySeq = 0;
function nextEntryKey() {
  entryKeySeq += 1;
  return `${Date.now()}-${entryKeySeq}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Mint a client message id for a NEW logical bug report, before its first
 * submit. Returns null where crypto.randomUUID is unavailable (non-secure
 * contexts) — the report still submits and still queues, it just has no
 * server-side idempotency key.
 */
export function newClientMessageId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return null;
}
// Cap so a learner who is offline for a long stretch (or a persistently
// failing retry) can't grow this without bound across sessions.
const MAX_PENDING_REPORTS = 10;

function readPendingQueue() {
  try {
    const raw = localStorage.getItem(PENDING_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_e) {
    // localStorage disabled / parse error — degrade to "nothing pending".
    return [];
  }
}

// Returns true on a successful write, false if it degraded (quota / private
// browsing / localStorage disabled). Callers that can retry a smaller payload
// (see enqueuePendingBugReport) use the return value to do so; nothing else
// depends on it, matching useDraftPersistence's "never throw" contract.
function writePendingQueue(queue) {
  try {
    localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(queue));
    return true;
  } catch (_e) {
    // Quota exceeded / private browsing — degrade silently, same contract
    // as useDraftPersistence.
    return false;
  }
}

/**
 * Reports currently queued for retry. Each entry:
 *   { clientMessageId, payload, queuedAt }
 * Absence of an entry means either "never failed" or "already confirmed and
 * cleared" — the two are indistinguishable from this store alone by design
 * (same as a cleared chat draft), which is why `payload` is kept in full
 * inside the entry rather than referencing something that could go stale.
 */
export function getPendingBugReports() {
  return readPendingQueue();
}

/**
 * Queue a bug report payload for retry after a failed submit. Generates a
 * clientMessageId (UUID v4) if the caller hasn't already reused one from a
 * prior attempt, so a subsequent retry of the SAME logical report hits the
 * server-side dedup (Migration 0409) instead of creating a duplicate row.
 * Returns the clientMessageId used (null if crypto.randomUUID is
 * unavailable — the report still queues, just without idempotency).
 */
export function enqueuePendingBugReport(payload, clientMessageId = null, ownerId = null) {
  const id = clientMessageId || newClientMessageId();
  const entryKey = nextEntryKey();
  try {
    // Only de-duplicate against a REAL id. Filtering on a null id matched every
    // other null-id entry and wiped the queue (defect (b) above).
    const queue = id
      ? readPendingQueue().filter((item) => item.clientMessageId !== id)
      : readPendingQueue();
    queue.push({ entryKey, ownerId: ownerId ?? null, clientMessageId: id, payload, queuedAt: new Date().toISOString() });
    while (queue.length > MAX_PENDING_REPORTS) queue.shift();
    const wrote = writePendingQueue(queue);
    // The screenshot is by far the largest field (base64, up to ~2MB) and the
    // least essential to keep — losing the note text is the failure the
    // learner actually experiences (#2611); losing the screenshot on retry
    // is a minor UX regression, not data loss. If the full payload didn't
    // fit (quota), retry once without it before giving up.
    if (!wrote && payload?.screenshot) {
      const withoutScreenshot = queue.map((item) =>
        item.entryKey === entryKey ? { ...item, payload: { ...item.payload, screenshot: null } } : item
      );
      writePendingQueue(withoutScreenshot);
    }
  } catch (_e) {
    // Never throw from the persistence path — matches useDraftPersistence's
    // silent-degrade contract.
  }
  return id;
}

function dequeuePendingBugReportByEntryKey(entryKey) {
  try {
    writePendingQueue(readPendingQueue().filter((item) => item.entryKey !== entryKey));
  } catch (_e) {
    /* ignore */
  }
}

/**
 * Attempt to resend every queued report. An item is removed from the queue
 * ONLY after the server confirms (2xx) — a failure (still offline, or a new
 * network error) leaves it queued for the next flush. Call on app load
 * (Issue #2611's "flush on next app load") and opportunistically when the
 * browser regains connectivity.
 *
 * Returns { sent, remaining } so callers can surface UX (e.g. "2 saved
 * reports just sent").
 */
export async function flushPendingBugReports(ownerId = null) {
  // Send ONLY this user's own queued reports. The queue is per-browser and
  // survives sign-out, so without this filter a queued report would be
  // re-submitted under whichever account happens to be signed in when the
  // flush next runs, and the server attributes the row purely from the session
  // (routes/bugReports.js) — filing user A's report, screenshot and metadata
  // as user B's, in B's organization (defect (a) above).
  //
  // A null ownerId matches only entries that also have none: those predate
  // this fix, and since they cannot be attributed to anyone they are never
  // sent. They are dropped instead, so an un-ownable report cannot sit in the
  // queue forever leaking A's payload into every future flush attempt.
  const queue = readPendingQueue();
  const mine = queue.filter((item) => (item.ownerId ?? null) === (ownerId ?? null) && item.ownerId != null);
  const unownable = queue.filter((item) => item.ownerId == null);
  if (unownable.length) {
    writePendingQueue(queue.filter((item) => item.ownerId != null));
  }
  let sent = 0;
  for (const item of mine) {
    try {
      // eslint-disable-next-line no-await-in-loop -- sequential by design: each
      // resubmit must confirm (and dequeue) before the next is attempted, so a
      // still-offline learner doesn't fire N parallel doomed requests.
      await submitBugReport({ ...item.payload, clientMessageId: item.clientMessageId });
      dequeuePendingBugReportByEntryKey(item.entryKey);
      sent += 1;
    } catch (_err) {
      // Still failing — leave it queued, try again on the next flush.
    }
  }
  return { sent, remaining: readPendingQueue().length, discarded: unownable.length };
}

/** True for the network-failure class this queue exists to catch (safeFetch's
 * FetchError{status:0} — see utils/safeFetch.ts). Distinguishes "lost on the
 * wire, retry silently" from a real validation/rate-limit error the learner
 * needs to see and act on (e.g. 400/429). */
export function isNetworkFailure(err) {
  return !!err && err.status === 0;
}
